// - Hosts the marketing landing experience, CTA flows, and supporting modals.
// - Connects analytics events to CTA interactions and example report previews.
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Share2 } from 'lucide-react'

import { Button } from './components/ui/button'
import { Logo } from './components/Logo'
import { AboutModal } from './components/AboutModal'
import { ShareModal } from './components/ShareModal'
import { ExampleReport } from './components/ExampleReport'
import AIAgentsVisualization from './components/AIAgentsVisualization'

import './App.css'

const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/cNibJ2ffGfGtfA71xfenS04'

function App() {
  const [isVisible, setIsVisible] = useState(false)
  const [aboutModalOpen, setAboutModalOpen] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [exampleReportOpen, setExampleReportOpen] = useState(false)
  const [isMarqueePaused, setIsMarqueePaused] = useState(false)

  const earlyFeedback = [
    {
      quote: 'Used the Eagles vs Packers report last week and it called out the defensive matchup advantage that nobody was talking about. Took Eagles ML and cashed easy. Felt like I knew something the bookies missed.',
      role: 'Early access user',
      color: 'from-[#00E5FF]/30 to-[#00E5FF]/5 border-[#00E5FF]/20'
    },
    {
      quote: 'First time I actually felt confident placing a bet instead of just hoping. The injury analysis showed me why the line was off and I jumped on it. Hit on 3 out of 4 picks that slate.',
      role: 'Weekend bettor',
      color: 'from-purple-500/30 to-purple-500/5 border-purple-500/20'
    },
    {
      quote: 'Been betting casually for years but always felt like I was guessing. Now I read the report, see the edges they found, and suddenly it clicks. Made more profit this month than the last 6 combined.',
      role: 'Recreational bettor',
      color: 'from-green-500/30 to-green-500/5 border-green-500/20'
    },
    {
      quote: "The weather breakdown for that Chiefs game saved me from a bad bet. Everyone was on the over but the report explained why the wind would kill it. Took the under and it wasn't even close.",
      role: 'Demo user',
      color: 'from-orange-500/30 to-orange-500/5 border-orange-500/20'
    },
    {
      quote: 'I used to spend hours reading articles and still felt lost. Now I get one report and know exactly what matters. Hit my first parlay last week because I actually understood the matchups.',
      role: 'New bettor',
      color: 'from-blue-500/30 to-blue-500/5 border-blue-500/20'
    },
    {
      quote: "The best part is feeling like I have an edge. My buddies are still guessing based on records and I'm looking at stuff they don't even know exists. Already up 40% this season.",
      role: 'Beta tester',
      color: 'from-pink-500/30 to-pink-500/5 border-pink-500/20'
    }
  ]

  const feedbackLoop = [...earlyFeedback, ...earlyFeedback]

  const handleGetAccess = () => {
    // Track Google Analytics event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'engagement',
        event_label: 'preorder_access',
        value: 10
      })
      
      // Track conversion event
      window.gtag('event', 'PURCHASE', {
        event_category: 'conversion',
        event_label: 'preorder_payment',
        value: 10,
        currency: 'USD'
      })
    }
    
    setShareModalOpen(true)
    window.open(STRIPE_PAYMENT_LINK, '_blank', 'noopener,noreferrer')
  }

  const handleViewReport = () => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'view_example_report', {
        event_category: 'engagement',
        event_label: 'patriots_jets_preview'
      })
    }

    setExampleReportOpen(true)
  }

  const handleShareClick = () => {
    // Track Google Analytics event for share button click
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'share_modal_open', {
        event_category: 'engagement',
        event_label: 'share_button_click'
      })
    }
    
    setShareModalOpen(true)
  }

  useEffect(() => {
    setIsVisible(true)
    document.documentElement.classList.add('dark')
  }, [])

  // Check for query parameters on mount and when URL changes
  useEffect(() => {
    const checkQueryParams = () => {
      const params = new URLSearchParams(window.location.search)
      const hasShare = params.get('share') === 'true'
      
      if (hasShare) {
        setShareModalOpen(true)
      }
      
      // Clean up URL without reloading (remove query params after opening modals)
      if (hasShare) {
        window.history.replaceState({}, '', window.location.pathname)
      }
    }

    checkQueryParams()

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', checkQueryParams)
    
    return () => {
      window.removeEventListener('popstate', checkQueryParams)
    }
  }, [])

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-black/90 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex h-auto min-h-16 sm:min-h-20 items-center justify-between gap-3 py-2 sm:py-0">
            <Logo />
            <div className="flex w-full max-w-md items-center gap-2 sm:max-w-none sm:justify-end sm:gap-3">
              <Button 
                size="sm" 
                variant="ghost"
                className="text-white hover:bg-white/10 border border-white/10 h-9 sm:h-10 w-9 sm:w-10 p-0"
                onClick={handleShareClick}
                aria-label="Share"
              >
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>

              {/* Mobile CTA Stack */}
              <div className="flex flex-1 gap-2 sm:hidden">
                <Button 
                  size="sm" 
                  className="flex-1 bg-white text-black font-medium px-3 text-xs h-9"
                  onClick={handleViewReport}
                >
                  View Report
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black font-medium px-3 text-xs h-9"
                  onClick={handleGetAccess}
                >
                  Pre-Order
                </Button>
              </div>

              {/* Desktop CTA Inline */}
              <div className="hidden sm:flex items-center gap-3">
                <Button 
                  size="sm" 
                  className="bg-white text-black font-medium px-4 sm:px-5 text-xs sm:text-sm h-9 sm:h-10"
                  onClick={handleViewReport}
                >
                  View Patriots vs Jets Report
                </Button>
                <Button 
                  size="sm" 
                  className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black font-medium px-4 sm:px-6 text-xs sm:text-sm h-9 sm:h-10"
                  onClick={handleGetAccess}
                >
                  Pre-Order Early Access
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-32 min-h-screen flex items-center overflow-hidden">
        {/* AI Agents Visualization - Background Element */}
        <AIAgentsVisualization />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
          <div className={`max-w-5xl transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="mb-6 sm:mb-8">
              <div className="inline-block border border-white/10 px-3 py-1 text-xs tracking-widest text-gray-500 uppercase mb-8 sm:mb-12">
                AI Sports Intelligence
              </div>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold mb-8 sm:mb-12 leading-[0.95] tracking-tight">
              Stop guessing.
              <br />
              <span className="text-[#00E5FF]">Start winning.</span>
            </h1>
            
            <div className="max-w-xl mb-12 sm:mb-16">
              <p className="text-lg sm:text-xl text-gray-400 leading-relaxed mb-6 sm:mb-8">
                Access cutting-edge intelligence that no one else has. Our AI uncovers hidden patterns and insights from hundreds of data sources, giving you the information edge professionals rely on to make highly educated decisions.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button 
                  size="lg" 
                  className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black font-medium px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base w-full sm:w-auto"
                  onClick={handleViewReport}
                >
                  View Patriots vs Jets Report
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/10 hover:bg-white/10 text-white font-medium px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base w-full sm:w-auto"
                  onClick={handleGetAccess}
                >
                  Pre-Order Early Access
                </Button>
              </div>
            </div>

            {/* Minimal Stats */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 lg:gap-12 max-w-2xl pt-8 sm:pt-12 border-t border-white/5">
              {[
                { value: '100+hrs', label: 'Intelligence per report' },
                { value: 'All games', label: 'Exclusive insights' },
                { value: '24/7', label: 'Comprehensive intelligence' }
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 uppercase tracking-wide">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Early Feedback */}
      <section className="py-16 sm:py-24 lg:py-32 border-t border-white/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-12 sm:mb-16 lg:mb-20">
              Early Feedback
            </h2>
            <p className="text-gray-500 text-base sm:text-lg leading-relaxed mb-12 sm:mb-16 lg:mb-20">
              We've been sharing early demos and gathering reactions from users and investors.
            </p>

            <div 
              className="relative overflow-hidden"
              onMouseEnter={() => setIsMarqueePaused(true)}
              onMouseLeave={() => setIsMarqueePaused(false)}
            >
              <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black via-black/70 to-transparent pointer-events-none z-10" />
              <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black via-black/70 to-transparent pointer-events-none z-10" />

              <div className={`flex gap-6 sm:gap-8 animate-feedback-marquee ${isMarqueePaused ? 'pause-animation' : ''}`}>
                {feedbackLoop.map((item, i) => (
                  <article
                    key={`${item.role}-${i}`}
                    className="w-[280px] sm:w-[300px] flex-shrink-0 border border-white/10 rounded-2xl bg-black/50 px-5 py-6 sm:px-6 sm:py-7 shadow-lg shadow-black/40 hover:border-white transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${item.color} flex-shrink-0`} />
                      <div className="font-heading text-sm text-white leading-tight">
                        {item.role}
                      </div>
                    </div>

                    <p className="text-sm sm:text-base text-gray-300 leading-relaxed break-words">
                      "{item.quote}"
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-24 lg:py-32 border-t border-white/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-12 sm:mb-16 lg:mb-20">
              The problem
            </h2>

            <div className="space-y-10 sm:space-y-12 lg:space-y-16">
              {[
                {
                  title: 'Information overload',
                  desc: 'Critical insights buried across hundreds of sources—injury reports, weather patterns, advanced metrics, player trends, historical data'
                },
                {
                  title: 'Time intensive',
                  desc: 'Professionals spend hundreds of hours analyzing each game slate. You have minutes, not hours, to gain the same edge'
                },
                {
                  title: 'Missing the edge',
                  desc: 'Without deep analysis, you miss the hidden patterns and correlations that separate informed decisions from guesswork'
                },
                {
                  title: 'No synthesis',
                  desc: 'Raw statistics without expert analysis leave you with data, not intelligence—unable to see what really matters'
                }
              ].map((problem, i) => (
                <div key={i} className="border-l-2 border-white/10 pl-4 sm:pl-6 lg:pl-8 hover:border-[#00E5FF] transition-colors">
                  <h3 className="font-heading text-xl sm:text-2xl font-bold mb-2 sm:mb-3">{problem.title}</h3>
                  <p className="text-gray-500 text-base sm:text-lg leading-relaxed max-w-2xl">
                    {problem.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-12 sm:mb-16 lg:mb-20">
              The solution
            </h2>

            <div className="space-y-1 mb-12 sm:mb-16 lg:mb-20">
              {[
                { agent: 'Injury & Availability Scout', task: 'Official reports, load management, travel, fatigue flags' },
                { agent: 'News & Sentiment Scanner', task: 'Beat reports, pressers, social, locker room signals' },
                { agent: 'Tactics & Film Analyst', task: 'Schemes, matchups, coverage tendencies, exploitable edges' },
                { agent: 'Advanced Metrics & Modeling Engine', task: 'Play-by-play data, projections, simulations, true-price lines' },
                { agent: 'Market Intelligence Tracker', task: 'Line movement, limits, sharp vs public splits, derivative markets' },
                { agent: 'Environment & Officiating Monitor', task: 'Weather, surface, stadium factors, ref tendencies, pace impact' },
                { agent: 'Master Integrator', task: 'Aggregates all outputs into ranked edges and risk-weighted positions' }
              ].map((agent, i) => (
                <div key={i} className="border-b border-white/5 py-4 sm:py-6 hover:border-[#00E5FF]/30 transition-colors group">
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 sm:gap-4 lg:gap-8">
                    <span className="font-heading text-lg sm:text-xl font-medium group-hover:text-[#00E5FF] transition-colors">
                      {agent.agent}
                    </span>
                    <span className="text-gray-600 text-sm sm:text-right max-w-md">
                      {agent.task}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-gray-500 text-base sm:text-lg leading-relaxed">
              Seven specialized AI agents work in parallel, each uncovering insights others miss. 
              The result: intelligence that would take professionals hundreds of hours to compile—giving you an information advantage on every game.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 lg:py-32 border-t border-white/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-12 sm:mb-16 lg:mb-20">
              What you get
            </h2>

            <div className="grid md:grid-cols-2 gap-x-8 sm:gap-x-12 lg:gap-x-20 gap-y-10 sm:gap-y-12 lg:gap-y-16">
              {[
                {
                  title: 'Exclusive intelligence per game',
                  desc: 'Unique insights synthesized from hundreds of data sources—player dynamics, injury impacts, environmental factors, historical patterns, and strategic recommendations'
                },
                {
                  title: 'Uncover hidden edges',
                  desc: 'AI-powered analysis reveals correlations and patterns invisible to the naked eye—giving you insights that separate informed decisions from guesswork'
                },
                {
                  title: 'Professional-grade analysis',
                  desc: 'Each report delivers intelligence that would take analysts hundreds of hours to compile—automatically generated for every game you follow'
                },
                {
                  title: 'Multi-sport coverage',
                  desc: 'NFL, NCAA Football, NBA, MLB, and more. Year-round cutting-edge intelligence across all major leagues and matchups'
                },
                {
                  title: 'Latest intelligence',
                  desc: 'Comprehensive analysis when developments impact your research—injury updates, weather shifts, lineup changes, and more'
                },
                {
                  title: 'Mobile access',
                  desc: 'Access exclusive insights anywhere, anytime. Your competitive intelligence advantage, always at your fingertips'
                }
              ].map((feature, i) => (
                <div key={i} className="group">
                  <h3 className="font-heading text-lg sm:text-xl font-bold mb-2 sm:mb-3 group-hover:text-[#00E5FF] transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 text-sm sm:text-base leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-12 sm:mb-16 lg:mb-20">
              How it works
            </h2>

            <div className="space-y-8 sm:space-y-10 lg:space-y-12">
              {[
                {
                  step: '01',
                  title: 'Select your sports',
                  desc: 'Choose the leagues you want to follow. Get exclusive intelligence reports for every upcoming matchup.'
                },
                {
                  step: '02',
                  title: 'Receive intelligence reports',
                  desc: 'Each game gets a comprehensive analysis with insights no one else has. Our AI reveals hidden patterns and correlations invisible to traditional analysis—player dynamics, advanced metrics, injury impacts, environmental factors, historical patterns.'
                },
                {
                  step: '03',
                  title: 'Make educated decisions',
                  desc: 'Use professional-grade intelligence to see what others miss. Gain the information edge to make highly educated decisions based on insights, not guesswork.'
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 sm:gap-6 lg:gap-12 items-start group border-b border-white/5 pb-8 sm:pb-10 lg:pb-12 last:border-0">
                  <div className="flex-shrink-0">
                    <div className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white/5 group-hover:text-[#00E5FF]/20 transition-colors">
                      {item.step}
                    </div>
                  </div>
                  <div className="flex-1 pt-1 sm:pt-2">
                    <h3 className="font-heading text-xl sm:text-2xl font-bold mb-2 sm:mb-3">{item.title}</h3>
                    <p className="text-gray-500 text-base sm:text-lg leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Stats */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
              {[
                { value: '200+', label: 'Games analyzed weekly' },
                { value: '100+hrs', label: 'Intelligence per report' },
                { value: 'Exclusive', label: 'Insights no one else has' },
                { value: '24/7', label: 'Comprehensive intelligence' }
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-white mb-1 sm:mb-2">
                    {stat.value}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 uppercase tracking-wide">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-32 lg:py-40 border-t border-white/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-8 sm:mb-10 lg:mb-12 leading-tight">
              Gain an unfair advantage
            </h2>
            <p className="text-lg sm:text-xl text-gray-500 mb-8 sm:mb-10 lg:mb-12 max-w-2xl">
              Secure early access to intelligence that no one else has. Get cutting-edge insights and hundreds of hours of analysis for every game—giving you the information advantage to make highly educated decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black font-medium px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base w-full sm:w-auto"
                onClick={handleViewReport}
              >
                View Patriots vs Jets Report
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/10 hover:bg-white/10 text-white font-medium px-6 sm:px-8 h-11 sm:h-12 text-sm sm:text-base w-full sm:w-auto"
                onClick={handleGetAccess}
              >
                Pre-Order Early Access
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 sm:py-16 lg:py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 mb-12 sm:mb-14 lg:mb-16">
              <div>
                <Logo className="mb-4 sm:mb-6" />
                <p className="text-xs sm:text-sm text-gray-600">
                  AI-powered sports intelligence platform
                </p>
              </div>
              
              <div>
                <h4 className="font-heading text-xs sm:text-sm font-medium mb-3 sm:mb-4 uppercase tracking-wider text-gray-500">Company</h4>
                <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-400">
                  <li>
                    <button 
                      onClick={() => setAboutModalOpen(true)}
                      className="hover:text-white transition-colors text-left"
                    >
                      About
                    </button>
                  </li>
                  <li>
                    <Link to="/articles" className="hover:text-white transition-colors">
                      Articles
                    </Link>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-heading text-xs sm:text-sm font-medium mb-3 sm:mb-4 uppercase tracking-wider text-gray-500">Legal</h4>
                <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-400">
                  <li><a href="/terms.html" target="_blank" className="hover:text-white transition-colors">Terms</a></li>
                  <li><a href="/privacy.html" target="_blank" className="hover:text-white transition-colors">Privacy</a></li>
                </ul>
              </div>
            </div>
            
            <div className="pt-6 sm:pt-8 border-t border-white/5">
              <p className="text-xs sm:text-sm text-gray-600">
                © 2024 PitchMetric. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* About Modal */}
      <AboutModal open={aboutModalOpen} onOpenChange={setAboutModalOpen} />
      
      {/* Share Modal */}
      <ShareModal open={shareModalOpen} onOpenChange={setShareModalOpen} />

      {/* Example Report Modal */}
      <ExampleReport open={exampleReportOpen} onOpenChange={setExampleReportOpen} />
    </div>
  )
}

export default App
